import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { TrackingLink } from "@/components/TrackingLink";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UsmcaCheckbox } from "@/components/UsmcaCheckbox";
import { printUsmca } from "@/lib/usmca";

// ShipStation label dialog — creates a shipment + downloads the returned
// base64 PDF label. Address is pre-filled from the structured Shopify
// shipping_address on the order (falls back to parsing the flat
// customer.address string only for legacy / manually-entered orders).
// Shared between Back Office (Orders Received) and Inventory Packing wizard.
export default function ShipStationDialog({ order, open, onOpenChange, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [usmca, setUsmca] = useState(false);

  // Prefer the structured shipping address captured directly from Shopify.
  // Falls back to comma-parsing customer.address for legacy orders.
  const sa = order.shipping_address || null;
  const cust = order.customer || {};
  let init;
  if (sa) {
    init = {
      to_name: sa.name || cust.name || "",
      to_phone: sa.phone || cust.phone || "",
      to_street1: sa.street1 || "",
      to_street2: sa.street2 || "",
      to_city: sa.city || "",
      to_state: sa.state || "",
      to_postal: sa.postal || "",
      to_country: (sa.country || "US").toUpperCase().slice(0, 2),
    };
  } else {
    // Legacy parse: smarter than raw index positions — detect country,
    // postal, and state via regex hints so an address like
    //   "5614 34th Ave Apt 4b, Illinois, 61265, United States"
    // fills city="", state="Illinois", postal="61265", country="US" instead
    // of the old off-by-one garbage (city=Illinois, state=61265, ...).
    const parseFlatAddress = (raw) => {
      const tokens = String(raw || "").split(",").map((p) => p.trim()).filter(Boolean);
      const out = { street1: "", street2: "", city: "", state: "", postal: "", country: "" };
      if (!tokens.length) return out;

      const COUNTRY_MAP = {
        US: "US", USA: "US", "UNITED STATES": "US", "UNITED STATES OF AMERICA": "US",
        CA: "CA", CANADA: "CA", MX: "MX", MEXICO: "MX",
        GB: "GB", UK: "GB", "UNITED KINGDOM": "GB",
      };
      const last = tokens[tokens.length - 1].toUpperCase();
      if (COUNTRY_MAP[last]) {
        out.country = COUNTRY_MAP[last];
        tokens.pop();
      } else if (/^[A-Z]{2}$/i.test(last)) {
        out.country = last;
        tokens.pop();
      }

      // Postal patterns: US 5-digit (with optional +4) or Canadian A#A #A#.
      const postalRe = /^(\d{5}(?:-\d{4})?|[A-Z]\d[A-Z]\s?\d[A-Z]\d)$/i;
      if (tokens.length && postalRe.test(tokens[tokens.length - 1])) {
        out.postal = tokens.pop().toUpperCase();
      }

      if (tokens.length) out.state = tokens.pop();

      // Remaining tokens: last one is city, everything before is street.
      if (tokens.length === 1) {
        out.street1 = tokens[0];
      } else if (tokens.length >= 2) {
        out.city = tokens.pop();
        out.street1 = tokens.join(", ");
      }
      return out;
    };
    const parsed = parseFlatAddress(cust.address);
    init = {
      to_name: cust.name || "",
      to_phone: cust.phone || "",
      to_street1: parsed.street1,
      to_street2: parsed.street2,
      to_city: parsed.city,
      to_state: parsed.state,
      to_postal: parsed.postal,
      to_country: parsed.country || "US",
    };
  }

  const [form, setForm] = useState({
    carrier_code: "canada_post_walleted",
    service_code: "expedited_parcel",
    package_code: "package",
    // Preset package label — dimensions/weight refined later per user
    package_type: "",
    weight_value: 4,
    weight_units: "pounds",
    length: 12,
    width: 10,
    height: 6,
    dim_units: "inches",
    ...init,
  });

  useEffect(() => {
    let ok = true;
    api.get("/shipstation/status").then((r) => { if (ok) setStatus(r.data); }).catch(() => {});
    return () => { ok = false; };
  }, []);

  // Live carrier list — populated from the account
  const [carriers, setCarriers] = useState([]);
  const [services, setServices] = useState([]);
  useEffect(() => {
    let ok = true;
    api.get("/shipstation/carriers").then((r) => { if (ok) setCarriers(r.data || []); }).catch(() => {});
    return () => { ok = false; };
  }, []);
  useEffect(() => {
    if (!form.carrier_code) return;
    let ok = true;
    api.get("/shipstation/services", { params: { carrier_code: form.carrier_code } })
      .then((r) => { if (ok) setServices(r.data || []); })
      .catch(() => { if (ok) setServices([]); });
    return () => { ok = false; };
  }, [form.carrier_code]);

  // Package presets — fetched from the backend (built-in defaults + any
  // custom ones the user has saved). Selecting one auto-fills the
  // dimensions + weight below (still editable per shipment).
  const [packages, setPackages] = useState([]);
  const refreshPackages = React.useCallback(() => {
    return api.get("/shipstation/packages")
      .then((r) => setPackages(r.data || []))
      .catch(() => setPackages([]));
  }, []);
  useEffect(() => { refreshPackages(); }, [refreshPackages]);

  const applyPackage = (name) => {
    const p = packages.find((x) => x.name === name);
    if (!p) return setForm((f) => ({ ...f, package_type: name }));
    setForm((f) => ({
      ...f,
      package_type: p.name,
      length: p.length,
      width: p.width,
      height: p.height,
      weight_value: p.weight_value,
      weight_units: p.weight_units || f.weight_units,
      dim_units: p.dim_units || f.dim_units,
    }));
  };

  // "+ Add Package" mini-form state
  const [addOpen, setAddOpen] = useState(false);
  const [newPkg, setNewPkg] = useState({ name: "", length: "", width: "", height: "", weight_value: "" });
  const [addSaving, setAddSaving] = useState(false);
  const savePackage = async () => {
    const n = (newPkg.name || "").trim();
    if (!n) { toast.error("Name is required"); return; }
    const L = Number(newPkg.length), W = Number(newPkg.width), H = Number(newPkg.height), Wt = Number(newPkg.weight_value);
    if (!Number.isFinite(L) || !Number.isFinite(W) || !Number.isFinite(H) || !Number.isFinite(Wt) || L <= 0 || W <= 0 || H <= 0 || Wt <= 0) {
      toast.error("Length / Width / Height / Weight must all be positive numbers"); return;
    }
    setAddSaving(true);
    try {
      const r = await api.post("/shipstation/packages", {
        name: n, length: L, width: W, height: H, weight_value: Wt,
        weight_units: "pounds", dim_units: "inches",
      });
      toast.success(`Package '${r.data.name}' saved`);
      await refreshPackages();
      // Apply directly from the response — refreshPackages() may not have
      // resolved into state by the time applyPackage(name) runs, so the
      // lookup would miss. Setting the form fields explicitly is safer.
      setForm((f) => ({
        ...f,
        package_type: r.data.name,
        length: r.data.length,
        width: r.data.width,
        height: r.data.height,
        weight_value: r.data.weight_value,
        weight_units: r.data.weight_units || f.weight_units,
        dim_units: r.data.dim_units || f.dim_units,
      }));
      setAddOpen(false);
      setNewPkg({ name: "", length: "", width: "", height: "", weight_value: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save package");
    } finally {
      setAddSaving(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    if (usmca) printUsmca(order);
    try {
      const r = await api.post(`/orders/${order.id}/shipstation-label`, form);
      setResult(r.data);
      toast.success(`Label created · ${r.data.tracking_number || "no tracking returned"}`);
      // Optional callback fires AFTER the label is safely persisted — used by
      // the Inventory Packing wizard to auto-finalise packing (transitions
      // the order to Shipping) so the operator never has to click a second
      // "send to shipping" button.
      if (typeof onSuccess === "function") {
        try { await onSuccess(r.data); } catch (_) { /* swallow — label still created */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ShipStation call failed");
    } finally {
      setSaving(false);
    }
  };

  const download = () => {
    if (!result?.label_url) return;
    const a = document.createElement("a");
    a.href = result.label_url;
    a.download = `Label_${result.tracking_number || order.po_number}.pdf`;
    a.click();
  };

  const configured = status?.configured === true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="shipstation-dialog">
        <DialogHeader>
          <DialogTitle>Create ShipStation Label · {order.po_number}</DialogTitle>
        </DialogHeader>
        {!configured && (
          <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3 text-sm" data-testid="shipstation-not-configured">
            <div className="font-semibold mb-1">⚠ ShipStation is not connected yet.</div>
            <div className="text-xs">
              Add <code className="font-mono">SHIPSTATION_API_KEY</code> and <code className="font-mono">SHIPSTATION_API_SECRET</code> to <code className="font-mono">backend/.env</code>.
              Find them at: ShipStation → Settings (⚙) → Account → API Settings → Generate API Keys.
            </div>
          </div>
        )}
        <div className="space-y-3">
          {/* Carrier + Service — proper drop-downs fed live from ShipStation */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Carrier</Label>
              <select
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm font-mono"
                data-testid="ss-carrier"
                value={form.carrier_code}
                onChange={(e) => setForm({ ...form, carrier_code: e.target.value })}
              >
                {carriers.length === 0 && <option value={form.carrier_code}>{form.carrier_code}</option>}
                {carriers.map((c) => (
                  <option key={c.code} value={c.code}>{c.name} {c.balance != null ? `($${c.balance})` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Service</Label>
              <select
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm font-mono"
                data-testid="ss-service"
                value={form.service_code}
                onChange={(e) => setForm({ ...form, service_code: e.target.value })}
              >
                {services.length === 0 && <option value={form.service_code}>{form.service_code}</option>}
                {services.map((s) => (
                  <option key={s.code} value={s.code.trim()}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>Package</span>
                <button
                  type="button"
                  data-testid="ss-package-add-toggle"
                  onClick={() => setAddOpen((v) => !v)}
                  className="text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-slate-900"
                >
                  {addOpen ? "× cancel" : "+ Add package"}
                </button>
              </Label>
              <Select
                value={form.package_type || ""}
                onValueChange={applyPackage}
              >
                <SelectTrigger data-testid="ss-package-type" className="h-10 text-sm font-mono">
                  <SelectValue placeholder="Pick package…" />
                </SelectTrigger>
                <SelectContent>
                  {packages.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={p.name}
                      data-testid={`ss-package-option-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {p.name}
                      <span className="ml-2 text-[10px] text-slate-400 font-mono">
                        {p.length}×{p.width}×{p.height} · {p.weight_value}{p.weight_units === "pounds" ? "lb" : p.weight_units}
                      </span>
                    </SelectItem>
                  ))}
                  {packages.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">Loading packages…</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-slate-500 self-end pb-1 leading-snug">
              Picking a preset fills the dimensions + weight below (still editable per shipment).
            </div>
          </div>
          {addOpen && (
            <div
              data-testid="ss-add-package-form"
              className="rounded-md border-2 border-slate-900/10 bg-slate-50 p-3 space-y-2"
            >
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">New package preset</div>
              <div className="grid grid-cols-5 gap-2">
                <div className="col-span-2">
                  <Label className="text-[10px]">Name</Label>
                  <Input
                    data-testid="ss-add-package-name"
                    value={newPkg.name}
                    placeholder="e.g. Extra Large"
                    onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-[10px]">L (in)</Label>
                  <Input type="number" step="0.1" data-testid="ss-add-package-length" value={newPkg.length} onChange={(e) => setNewPkg({ ...newPkg, length: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px]">W (in)</Label>
                  <Input type="number" step="0.1" data-testid="ss-add-package-width" value={newPkg.width} onChange={(e) => setNewPkg({ ...newPkg, width: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px]">H (in)</Label>
                  <Input type="number" step="0.1" data-testid="ss-add-package-height" value={newPkg.height} onChange={(e) => setNewPkg({ ...newPkg, height: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-5 gap-2 items-end">
                <div>
                  <Label className="text-[10px]">Weight (lb)</Label>
                  <Input type="number" step="0.1" data-testid="ss-add-package-weight" value={newPkg.weight_value} onChange={(e) => setNewPkg({ ...newPkg, weight_value: e.target.value })} />
                </div>
                <div className="col-span-4 flex justify-end">
                  <Button
                    size="sm"
                    data-testid="ss-add-package-save"
                    disabled={addSaving}
                    onClick={savePackage}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {addSaving ? "Saving…" : "Save & Use"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            <div><Label className="text-xs">Weight</Label><Input type="number" step="0.1" value={form.weight_value} onChange={(e) => setForm({ ...form, weight_value: Number(e.target.value) })} data-testid="ss-weight" /></div>
            <div><Label className="text-xs">L</Label><Input type="number" value={form.length} onChange={(e) => setForm({ ...form, length: Number(e.target.value) })} data-testid="ss-length" /></div>
            <div><Label className="text-xs">W</Label><Input type="number" value={form.width} onChange={(e) => setForm({ ...form, width: Number(e.target.value) })} data-testid="ss-width" /></div>
            <div><Label className="text-xs">H</Label><Input type="number" value={form.height} onChange={(e) => setForm({ ...form, height: Number(e.target.value) })} data-testid="ss-height" /></div>
          </div>
          <div className="border-t pt-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-2">Ship To</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Recipient name</Label><Input value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} data-testid="ss-to-name" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.to_phone} onChange={(e) => setForm({ ...form, to_phone: e.target.value })} data-testid="ss-to-phone" /></div>
              <div className="col-span-2"><Label className="text-xs">Street 1</Label><Input value={form.to_street1} onChange={(e) => setForm({ ...form, to_street1: e.target.value })} data-testid="ss-to-street1" /></div>
              <div className="col-span-2"><Label className="text-xs">Street 2 (optional)</Label><Input value={form.to_street2} onChange={(e) => setForm({ ...form, to_street2: e.target.value })} data-testid="ss-to-street2" /></div>
              <div><Label className="text-xs">City</Label><Input value={form.to_city} onChange={(e) => setForm({ ...form, to_city: e.target.value })} data-testid="ss-to-city" /></div>
              <div><Label className="text-xs">State/Prov</Label><Input value={form.to_state} onChange={(e) => setForm({ ...form, to_state: e.target.value })} data-testid="ss-to-state" /></div>
              <div><Label className="text-xs">Postal code</Label><Input value={form.to_postal} onChange={(e) => setForm({ ...form, to_postal: e.target.value })} data-testid="ss-to-postal" /></div>
              <div><Label className="text-xs">Country (2-letter)</Label><Input value={form.to_country} onChange={(e) => setForm({ ...form, to_country: e.target.value.toUpperCase().slice(0, 2) })} data-testid="ss-to-country" /></div>
            </div>
          </div>
          {!result && <UsmcaCheckbox order={order} checked={usmca} onChange={setUsmca} testid="ss-usmca" />}
          {result ? (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3 text-sm" data-testid="ss-result">
              <div className="font-semibold text-emerald-900">✓ Label ready</div>
              <div className="text-xs font-mono mt-1">Tracking · {result.tracking_number ? <TrackingLink number={result.tracking_number} carrier={result.carrier_code || result.carrier} testid="shipstation-tracking-link" /> : "—"}</div>
              <div className="text-xs font-mono">Carrier · {result.carrier_code} / {result.service_code}</div>
              {result.test_label && <div className="text-[11px] mt-1 text-amber-800 font-semibold">TEST LABEL (no postage charged)</div>}
              <Button size="sm" className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={download} disabled={!result.label_url} data-testid="ss-download">
                ⬇ Download PDF
              </Button>
            </div>
          ) : (
            <Button
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              onClick={submit}
              disabled={saving || !configured}
              data-testid="ss-submit"
            >
              {saving ? "Contacting ShipStation…" : (configured ? "Create Label" : "ShipStation Not Connected")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
